import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import {
  isLastLink,
  SecondaryLinksComponentProps,
  SecondaryLinksProps, SecondaryLinksThemeProps,
} from '../../components/navigation/header/secondaryHeader/SecondaryLinks';
import { maxVisibleLinksOnLargeAndXLarge } from '../../components/navigation/header/secondaryHeader/SecondaryHeader';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { getGeneratedGlobalStyles } from '../../../typography/globalTypographyStyles';
import { AMPMoreLinks } from './AMPMoreLinks';

const componentConfigThemeKey = 'secondaryLinks';

const getTextStyle = (vrLineHeight: VrLineHeight, lastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: lastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: lastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px` : 0,
      },
    },
  });
};

export const getContainerStyle = (vrLineHeight: VrLineHeight) => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        overflowX: 'auto',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        overflowX: 'auto',
      },
    },
  });
};
const getCreateLinkFunction = (idleColor:string, activeStateColor:string, hoverStateColor:string, vrLineHeight:VrLineHeight, linksLength:number, pagePath:string) => {
  const createLink = (link:HeaderLinksDataProps, index:number) => {
    const { href, text } = link;
    const isActiveLink = href === pagePath;

    const linkStyleBasedOnScreen = linksLength > maxVisibleLinksOnLargeAndXLarge && index >= maxVisibleLinksOnLargeAndXLarge - 1
      ? {
        [MEDIA_BREAKPOINTS.LARGE]: {
          display: 'none',
        },
        [MEDIA_BREAKPOINTS.X_LARGE]: {
          display: 'none',
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          display: 'inline-flex',
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          display: 'inline-flex',
        },
      }
      : {};
    const linkStyle = generateLinkStyleFromTheme({
      idleColor: isActiveLink ? activeStateColor : idleColor,
      activeStateColor,
      hoverStateColor: isActiveLink ? activeStateColor : hoverStateColor,
    }, linkStyleBasedOnScreen);
    const style = getTextStyle(vrLineHeight, isLastLink(index, linksLength));
    const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());
    return (
      <TextLinkComponent key={index} href={href} linkStyle={linkStyle}>
        <span className={css(contentStyle.style, style.link)}>{text}</span>
      </TextLinkComponent>
    );
  };
  return createLink;
};

export const AMPSecondaryLinksComponent: React.FunctionComponent<SecondaryLinksComponentProps> = props => {
  const { idleColor, moreLinksText, vrLineHeight, activeStateColor, hoverStateColor, links, pagePath } = props;
  const moreLinks = links.length > maxVisibleLinksOnLargeAndXLarge ? links.slice(maxVisibleLinksOnLargeAndXLarge - 1) : [];
  const style = getContainerStyle(vrLineHeight);
  return (
    <React.Fragment>
      <div className={css(style.container)}>
        { links.map(getCreateLinkFunction(idleColor, activeStateColor, hoverStateColor, vrLineHeight, links.length, pagePath)) }
      </div>
      { moreLinks.length > 0 ? <AMPMoreLinks links={moreLinks} moreLinksText={moreLinksText} pagePath={pagePath} /> : null }
    </React.Fragment>
  );
};

const mapThemeProps = (theme: SecondaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    vrLineHeight: theme.vrLineHeight,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
  };
};

export const AMPSecondaryLinks = (props: SecondaryLinksProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <AMPSecondaryLinksComponent {...themeContext} {...props} />;
};
